import { useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useActiveTabInView } from '../../lib/useActiveTabInView';
import { KbReadinessCard } from '../dashboard/KbReadinessCard';
import { brandKindCopy } from '../onboarding/brandKind';
import { useBusiness } from '../onboarding/useBusiness';
import { AssetsSection } from './AssetsSection';
import { BrandIdentitySection } from './BrandIdentitySection';
import { BrandMemorySection } from './BrandMemorySection';
import { BrandVoiceSection } from './BrandVoiceSection';
import { InspirationSection } from './InspirationSection';
import { IntegrationsSection } from './IntegrationsSection';
import { LooksSection } from './LooksSection';
import { ProductsSection } from './ProductsSection';
import { ProofPointsSection } from './ProofPointsSection';
import {
  DEFAULT_KNOWLEDGE_BASE_SECTION,
  isKnowledgeBaseSection,
  KNOWLEDGE_BASE_SECTION_PARAM,
  KNOWLEDGE_BASE_SECTIONS,
  type KnowledgeBaseSection,
} from './sections';

function TabPanel({ tab, businessId }: { tab: KnowledgeBaseSection; businessId: string }) {
  switch (tab) {
    case 'brand-identity':
      return <BrandIdentitySection businessId={businessId} />;
    case 'looks':
      return <LooksSection businessId={businessId} />;
    case 'voice':
      return <BrandVoiceSection businessId={businessId} />;
    case 'products':
      return <ProductsSection businessId={businessId} />;
    case 'assets':
      return <AssetsSection businessId={businessId} />;
    case 'proof-points':
      return <ProofPointsSection businessId={businessId} />;
    case 'inspiration':
      return <InspirationSection businessId={businessId} />;
    case 'brand-memory':
      return <BrandMemorySection businessId={businessId} />;
    case 'integrations':
      return <IntegrationsSection businessId={businessId} />;
  }
}

/**
 * Brand profile (SRS §2): what the writer and designer know about the brand.
 * Six entity types, each a structured and individually queryable record, never
 * a free-text blob. Looks is a browse of the seeded slide themes whose only
 * write is the brand's default, Brand memory is the preferences every agent
 * reads, and Connected accounts holds the platform links publishing runs on.
 * Key dates live on the Calendar and copy checks run inside Review, so neither
 * has a tab here. News sources are managed on `/news`.
 *
 * Every call here is scoped to the active brand from `useBusiness`; the shell's
 * brand switcher is what changes it. Tab labels follow the brand's kind.
 *
 * The open section lives in the URL (`?section=proof-points`) so other screens,
 * the readiness card among them, can link straight at the gap they name. The
 * tabs are one row (sideways scroll on a phone), in the order words, proof,
 * looks, sources. The readiness card heads the page.
 */
export function KnowledgeBasePage() {
  const { business, businessId, isLoading, error } = useBusiness();
  const copy = brandKindCopy(business?.kind);
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get(KNOWLEDGE_BASE_SECTION_PARAM);
  const tab = isKnowledgeBaseSection(requested) ? requested : DEFAULT_KNOWLEDGE_BASE_SECTION;
  // On a phone the tab row scrolls sideways; a deep link may open a tab past its edge.
  const tabListRef = useRef<HTMLDivElement>(null);
  useActiveTabInView(tabListRef, tab);

  // A link to a section (from another screen or a readiness row) lands on the
  // tab row, not above the score card. A tab click marks itself and stays put.
  const location = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);
  const isDeepLink =
    isKnowledgeBaseSection(requested) &&
    (location.state as { kbTab?: boolean } | null)?.kbTab !== true;
  const hasTabs = !isLoading && businessId !== null;
  useEffect(() => {
    if (!isDeepLink || !hasTabs) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tabsRef.current?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, [location.key, isDeepLink, hasTabs]);

  function openSection(section: KnowledgeBaseSection) {
    // Replace, so browser back leaves the knowledge base instead of walking tabs.
    setSearchParams(
      { [KNOWLEDGE_BASE_SECTION_PARAM]: section },
      { replace: true, state: { kbTab: true } },
    );
  }

  return (
    <main className="page page--wide kb-page">
      <PageHeader
        section="Brand profile"
        title="Brand profile"
        lede={
          business
            ? `What we know about ${business.name} — the more that's here, the less your drafts guess.`
            : `What we know about your ${copy.noun} — the more that's here, the less your drafts guess.`
        }
      />

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your brand
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {error ??
              'No brand is linked to this account yet. Finish setup and the brand profile opens.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <>
          <KbReadinessCard businessId={businessId} />

          <div className="kb-tabs" ref={tabsRef}>
            <div
              className="tabs kb-tabs__tabs"
              role="tablist"
              aria-label="Brand profile sections"
              ref={tabListRef}
            >
              {KNOWLEDGE_BASE_SECTIONS.map(({ key }) => (
                <button
                  className={key === tab ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                  key={key}
                  type="button"
                  role="tab"
                  id={`kb-tab-${key}`}
                  aria-selected={key === tab}
                  aria-controls={`kb-panel-${key}`}
                  onClick={() => openSection(key)}
                >
                  {copy.sections[key]}
                </button>
              ))}
            </div>
          </div>

          <div
            className="tabs__panel"
            role="tabpanel"
            id={`kb-panel-${tab}`}
            aria-labelledby={`kb-tab-${tab}`}
          >
            <TabPanel tab={tab} businessId={businessId} />
          </div>
        </>
      )}
    </main>
  );
}
